import { Dispatch, FC, SetStateAction, MouseEvent, useState, useMemo } from 'react'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'

/* Utils */
import { CustomLabelCountI, CustomLabelI, SystemEventName } from 'models'
import { CUSTOM_LABEL_ID, getLabelActionProps, getSystemEventValue } from 'ui/pages/library/utils'
import { MAX_PINNED_LABELS_COUNT } from '../../../chart-utils'
import { dateFormat } from 'lib/utils'

/* Components */
import {
  LabelContainer,
  LabelHeader,
  LabelContent,
  LabelFooter,
  PrivacyIcon,
  TitleWrapper,
  IconWrapper,
  ActionButtons,
  Title,
  Description,
  MetaInfo,
  DateInfo
} from './drawerLabelCard.styles'
import LabelCardPinButton from './LabelCardPinButton'
import { PrivateLabelIcon, PublicLabelIcon } from 'ui/components/svg'
import CustomLabelDropdown from './CustomLabelDropdown'
import axios from 'lib/axios'
import { HotToast } from 'ui/components/toast'

interface LabelProps {
  label: CustomLabelI
  updateLabels: () => void
  customLabelsCount: CustomLabelCountI
  unpinLabel: (labelId: string) => Promise<void>
  pinLabel: (labelId: string) => Promise<void>
  pinningLabel: boolean
  setPinningLabel: Dispatch<SetStateAction<boolean>>
  updateEditLabel: (label: any) => void
  updateMode: (newMode: string) => void
  setLoading: Dispatch<SetStateAction<boolean>>
}

const DrawerLabelCard: FC<LabelProps> = ({
  label,
  updateLabels,
  customLabelsCount,
  unpinLabel,
  pinLabel,
  pinningLabel,
  setPinningLabel,
  updateEditLabel,
  updateMode,
  setLoading
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const customLabelDropdownOpen = Boolean(anchorEl)

  const isPinned = useMemo(() => {
    return !!customLabelsCount?.pinned_labels?.find((item) => item.id === label.id)
  }, [customLabelsCount])

  const togglePin = async () => {
    if (customLabelsCount?.pinned_labels.length >= MAX_PINNED_LABELS_COUNT && !isPinned) return

    try {
      setPinningLabel(true)

      if (isPinned) {
        await unpinLabel(label.id)
      } else {
        await pinLabel(label.id)
      }

      await updateLabels()
    } catch (e) {
      console.error(e)
      /* The pin limit is enforced server-side, so a refusal has to reach the user rather than the console. */
      toast.error(() => (
        <HotToast text={(e as any)?.response?.data?.message || `Failed to ${isPinned ? 'unpin' : 'pin'} label`} />
      ))
    } finally {
      setPinningLabel(false)
    }
  }

  const handleEdit = () => {
    handleMenuClose()
    updateMode('edit')
    updateEditLabel(label)
  }

  const handleDelete = async () => {
    handleMenuClose()
    setLoading(true)

    try {
      await axios.delete(`/api/v3/labels/cpis/custom/${label.id}`)
      await updateLabels()
      updateMode('show-0')
    } catch (e) {
      console.error(e)
      toast.error(() => <HotToast text={(e as any)?.response?.data?.message || 'Failed to delete label'} />)
    } finally {
      setLoading(false)
    }
  }

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const labelAction = getSystemEventValue(label.action)
  const { titleColor, icon } = getLabelActionProps(label.action)

  return (
    <LabelContainer>
      <LabelHeader>
        <TitleWrapper>
          <IconWrapper labelType={labelAction}>{icon}</IconWrapper>
          <Title titleColor={titleColor}>{label.action === CUSTOM_LABEL_ID ? label.data.title : labelAction}</Title>
        </TitleWrapper>

        <ActionButtons>
          <LabelCardPinButton
            isPinned={isPinned}
            togglePin={togglePin}
            customLabelsCount={customLabelsCount}
            pinningLabel={pinningLabel}
          />
          <CustomLabelDropdown
            labelAction={label.action}
            open={customLabelDropdownOpen}
            anchorEl={anchorEl}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        </ActionButtons>
      </LabelHeader>

      <LabelContent>
        <Description>
          {label.action === CUSTOM_LABEL_ID ? label.data.description : label.description?.replace(':', ':\n')}
        </Description>

        {label.action === SystemEventName.CpiCustom && label.username && (
          <MetaInfo>
            Created by: <span>{label.username}</span>
          </MetaInfo>
        )}
      </LabelContent>

      <LabelFooter>
        <DateInfo>{dayjs(label.data.date || label.created_at).format(dateFormat.short)}</DateInfo>
        <PrivacyIcon>
          {label.action !== SystemEventName.CpiCustom ? (
            <PublicLabelIcon />
          ) : label.data.is_public ? (
            <PublicLabelIcon />
          ) : (
            <PrivateLabelIcon />
          )}
        </PrivacyIcon>
      </LabelFooter>
    </LabelContainer>
  )
}

export default DrawerLabelCard
